import React, { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { ErrorMessage, Field, type FieldProps } from '@atlaskit/form';
import Select from '@atlaskit/select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { AtlaskitSelectInlineTabActivator } from '@atlassian/jira-inline-config-buttons-for-select/src/AtlaskitSelectInlineTabActivator.tsx';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type/index.tsx';
import type {
	IssueType,
	RequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import {
	ErrorBoundaryBypassInput,
	ErrorBoundaryBypassOutput,
	ErrorBoundaryBypassProvider,
	FieldBusyIndicator,
	UiModificationsErrorHandler,
} from '@atlassian/ui-modifications-core';
import { ISSUE_TYPE as ISSUE_TYPE_FIELDTYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { formatOptionLabel } from '@atlassian/jira-issue-field-select-base/src/ui/format-option-label/index.tsx';
import { GIC_VIEW_TYPE } from '@atlassian/jira-ui-modifications-view-gic/src/common/constants/index.tsx';
import { FieldValueDecoratorAsync } from '@atlassian/jira-ui-modifications-view-gic/src/ui/field-value-decorator/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { preventEscKeyPropagationKeyPressHandler } from '../../../../common/utils/index.tsx';
import { ISSUE_TYPE_PICKER_ELEMENT_ID } from './constants.tsx';
import type { PropsFromUIM } from './types.tsx';
import { ConfigureWorkTypePopup } from './configure-work-type/configure-work-type-popup/ConfigureWorkTypePopup.tsx';
import { useCustomOptionsAndComponentsForWorkTypeConfig } from './utils.tsx';

interface JiraWorkTypePickerComponentProps
	extends Omit<PropsFromUIM, 'gqlType' | 'fieldName' | 'fieldType' | 'fieldId' | 'isRequired'> {
	selectRef: React.RefObject<AtlaskitSelectRefType>;
	label: string;
	canConfigureIssueTypes: boolean;
	projectId: string;
	projectKey: string;
	selectedRequestOrIssueType: IssueType | RequestType | null;
	onIssueTypeCreatedOrUpdated?: () => void;
	onCloseConfigureWorkType: () => void;
	createIssueTypeButtonRef: React.RefObject<HTMLElement>;
	editIssueTypeButtonRef: React.RefObject<HTMLElement>;
	shouldTriggerModal: boolean;
	onClickCreateOrEditOption: (buttonName: 'createIssueTypeButton' | 'editIssueTypeButton') => void;
	isConfigureWorkTypePopupOpen: boolean;
	ConfigureWorkTypePopupContent: React.ComponentType;
	getIssueTypeHelpText: () => React.ReactElement;
	submitFieldwiseErrors?: Record<string, string>;
	isDisabled: boolean;
	onIssueTypeChange: (selectedIssueType: IssueType | RequestType | null) => void;
}

export const JiraWorkTypePickerComponent = ({
	areUiModificationsBusy,
	allowedValues,
	isReadOnly,
	selectRef,
	label,
	canConfigureIssueTypes,
	projectId,
	projectKey,
	selectedRequestOrIssueType,
	onIssueTypeCreatedOrUpdated,
	onCloseConfigureWorkType,
	createIssueTypeButtonRef,
	editIssueTypeButtonRef,
	shouldTriggerModal,
	onClickCreateOrEditOption,
	isConfigureWorkTypePopupOpen,
	ConfigureWorkTypePopupContent,
	getIssueTypeHelpText,
	submitFieldwiseErrors,
	isDisabled,
	onIssueTypeChange,
}: JiraWorkTypePickerComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { options, components, noOptionWrapperRef } =
		useCustomOptionsAndComponentsForWorkTypeConfig({
			options: allowedValues ?? [],
			canConfigureIssueTypes,
			projectId,
			projectKey,
			issueTypeId: selectedRequestOrIssueType?.value ?? '',
			onIssueTypeCreatedOrUpdated,
			onCreateModalClose: onCloseConfigureWorkType,
			onEditModalClose: onCloseConfigureWorkType,
			createIssueTypeButtonRef,
			editIssueTypeButtonRef,
			shouldTriggerModal,
			onClickCreateOrEditOption,
		});

	const [inputValue, setInputValue] = useState('');
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const handleIssueTypesDropdownOpen = () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'gicIssueTypeSelect open');
	};

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const fieldId = document.activeElement?.id;

	useEffect(() => {
		if (fieldId?.includes('type-picker') && fg('fix-gryf-a11y-issues')) {
			selectRef?.current?.focus();
		}
	}, [fieldId, selectRef]);
	const attributes = useMemo(
		() => ({
			projectId,
			isMenuOpen,
			fieldId,
		}),
		[fieldId, isMenuOpen, projectId],
	);

	return (
		<JiraIssueCreateErrorBoundary
			id="withFormField"
			packageName="jiraIssueCreateModal"
			sendToPrivacyUnsafeSplunk
			{...(fg('jira-issue-create-error-boundary-enabled') ? { attributes } : {})}
		>
			<Field
				name="type-picker"
				label={label}
				isRequired
				elementAfterLabel={
					areUiModificationsBusy ? <FieldBusyIndicator fieldId={ISSUE_TYPE_FIELDTYPE} /> : undefined
				}
			>
				{/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
				{({ fieldProps }: { fieldProps: FieldProps<any, HTMLInputElement> }) => {
					const renderFieldContent = ({
						id,
						isRequired, // eslint-disable-next-line @typescript-eslint/no-explicit-any
					}: FieldProps<any, HTMLInputElement>) => (
						<>
							{isConfigureWorkTypePopupOpen ? (
								<ConfigureWorkTypePopup
									renderPopupContent={() => <ConfigureWorkTypePopupContent />}
									onClose={onCloseConfigureWorkType}
									renderTarget={() => (
										// Minimal copy of Select used for popup anchor
										<Select
											menuIsOpen={false}
											value={selectedRequestOrIssueType}
											formatOptionLabel={formatOptionLabel}
											components={components}
										/>
									)}
								/>
							) : (
								<AtlaskitSelectInlineTabActivator
									setInputValue={setInputValue}
									setIsMenuOpen={setIsMenuOpen}
									inputValue={inputValue}
									isMenuOpen={isMenuOpen}
								>
									<Select
										ref={selectRef}
										inputId={id}
										menuIsOpen={isMenuOpen}
										inputValue={inputValue}
										required={isRequired}
										options={options}
										components={components}
										// @ts-expect-error - TS2322 - Type '(issueType: IssueType) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
										onChange={onIssueTypeChange}
										onMenuOpen={handleIssueTypesDropdownOpen}
										value={selectedRequestOrIssueType}
										isClearable={false}
										formatOptionLabel={formatOptionLabel}
										placeholder=""
										id={ISSUE_TYPE_PICKER_ELEMENT_ID}
										isDisabled={!!(isDisabled || isReadOnly)}
										onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
											preventEscKeyPropagationKeyPressHandler(e);
										}}
										onInputChange={(value, { action }) => {
											if (
												// This is to prevent the search value to be cleared when tabbing to the Create Issue Type button in Empty no results state
												(action !== 'input-blur' && action !== 'menu-close') ||
												!noOptionWrapperRef?.current
											) {
												setInputValue(value);
											}
										}}
									/>
								</AtlaskitSelectInlineTabActivator>
							)}
							{getIssueTypeHelpText()}
							{submitFieldwiseErrors?.issuetype && (
								<ErrorMessage>{submitFieldwiseErrors.issuetype}</ErrorMessage>
							)}
						</>
					);

					return (
						<ErrorBoundaryBypassProvider>
							<ErrorBoundaryBypassOutput />
							<JiraIssueCreateErrorBoundary
								id="issueAdjustmentsFieldValueDecorator"
								packageName="jiraIssueCreateModal"
								fallback={() => (
									<>
										<UiModificationsErrorHandler />
										{renderFieldContent(fieldProps)}
									</>
								)}
								sendToPrivacyUnsafeSplunk
								attributes={(error: Error) => ({
									errorType: getErrorType(error),
									viewType: GIC_VIEW_TYPE,
									fieldId: ISSUE_TYPE_FIELDTYPE,
									fieldType: ISSUE_TYPE_FIELDTYPE,
								})}
							>
								<FieldValueDecoratorAsync
									fieldId={ISSUE_TYPE_FIELDTYPE}
									fieldType={ISSUE_TYPE_FIELDTYPE}
									fieldProps={{
										...fieldProps,
										value: selectedRequestOrIssueType,
									}}
								>
									{(interceptedFieldProps) => (
										<ErrorBoundaryBypassInput>
											{renderFieldContent(
												// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
												interceptedFieldProps as FieldProps<
													// eslint-disable-next-line @typescript-eslint/no-explicit-any
													any,
													HTMLInputElement
												>,
											)}
										</ErrorBoundaryBypassInput>
									)}
								</FieldValueDecoratorAsync>
							</JiraIssueCreateErrorBoundary>
						</ErrorBoundaryBypassProvider>
					);
				}}
			</Field>
		</JiraIssueCreateErrorBoundary>
	);
};
